import AppContentManagerTable from "components/klaudsolcms/tables/AppContentManagerTable";
import BasicLayout from "components/layouts/BasicLayout";
import withAuth from "lib/withAuth";
import {
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableCaption,
  TableContainer,
  Flex,
  Button,
  Box,
  ResponsiveValue,
  useDisclosure,
  HStack,
  Tooltip,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useToast,
  Center,
  CircularProgress,
  Text,
  Heading,
  Code,
  Icon,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from "react-redux";
import { currentOrganizationSelector } from "features/me/meSlice";
import { collectionsSelector, deleteCollection, fetchCollections } from "features/collections/collectionSlice";
import DefaultFont from 'components/Fonts';
import * as BiIcon from 'react-icons/bi';
import { FaPlus } from "react-icons/fa";
import CollectionModal from "components/modals/CollectionModal";
import { BiHelpCircle, BiListUl, BiPencil, BiTrash } from "react-icons/bi";
import { useRef, useState } from "react";
import Collection from "backend/models/Collection";
import { AppDispatch } from "components/store";
import { useCurrentJWT } from "lib/AuthUtils";
import Link from "next/link";
import { useRouter } from "next/router";

export const thProps = {
  textTransform: 'none' as const,
  style: { ...DefaultFont.style },
  fontWeight: 'semibold',
  fontSize: '14px',
  letterSpacing: 'normal'
}

const columns = [
  { accessor: 'id', displayName: 'ID' },
  { accessor: 'name', displayName: 'Name' }
];

const values = [];


type DeleteDialogProps = {
  isOpen: boolean
  onClose: () => void,
  collection: Collection
}

const DeleteDialog = ({ isOpen, onClose, collection }: DeleteDialogProps) => {

  const cancelRef = useRef<any>();
  const toast = useToast();
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const jwt = useCurrentJWT();
  const organization_id = useSelector(currentOrganizationSelector());

  const onDelete = () => {
    (async () => {
      setLoading(true);
      await dispatch(deleteCollection({ jwt, organization_id, collection_id: collection.id }));
      await dispatch(fetchCollections({ jwt, organization_id }));

      setLoading(false);
      onClose();
      toast({
        title: 'Success',
        description: `Collection "${collection.name}" successfully deleted.`,
        status: 'success',
        isClosable: true,
      });

    })();
  }

  return (
    collection && (<AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold'>
            Delete Collection &ldquo;{collection.name}&rdquo;
          </AlertDialogHeader>

          <AlertDialogBody>
            <Text>Are you sure? You can&apos;t undo this action afterwards.</Text>
            <Text>All entries under this collection will be inaccessible as well.</Text>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Box mr={3}>
              {isLoading && <CircularProgress isIndeterminate color='app.primary' size='14px' />}
            </Box>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bgColor='app.danger'
              color='app.text.light'
              _hover={{ bgColor: 'app.danger', color: 'app.text.light' }}
              leftIcon={<BiTrash />}
              onClick={onDelete}
              ml={3}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    )
  )
}

function MyCollection() {



  const organization = useSelector(currentOrganizationSelector());
  const collections = useSelector(collectionsSelector(organization));
  const [currentCollection, setCurrentCollection] = useState<Collection | undefined>();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { onOpen: onDeleteDialogOpen, isOpen: isDeleteDialogOpen, onClose: onCloseDeleteDialog } = useDisclosure();
  const collectionDelete = (organizationId: string, collection: Collection) => {
    setCurrentCollection(collection);
    onDeleteDialogOpen();
  }
  const router = useRouter();

  const onClickCreateNewCollection = () => {
    setCurrentCollection(undefined);
    onOpen();
  }

  const onClickEditCollection = (collection: Collection) => () => {
    setCurrentCollection(collection);
    onOpen();
  }



  return (

    <BasicLayout>



      <Flex flexDirection='row-reverse' mt={50}>
        <Box>
          <Button
            bg='buttonBg.primary'
            color='button.primary'
            _hover={{ bg: 'buttonBg.primary' }}
            onClick={onClickCreateNewCollection}
            leftIcon={<FaPlus />}>
            Create new Collection
          </Button>
        </Box>
        <Box mr='auto'>
          <Heading>Create / Edit Collections</Heading>
        </Box>
      </Flex>

      <TableContainer mt={10} borderColor='app.tertiary' borderWidth='1px' borderRadius='3px'>
        <Table>
          <Thead>
            <Tr>
              <Th {...thProps}>Collection Name</Th>
              <Th {...thProps}>ID</Th>
              <Th {...thProps}>
                <Flex>
                  Order
                  <Tooltip hasArrow label="Those with lower order value will be displayed first." placement='top'>
                    <Box ml={1} _hover={{ cursor: 'pointer' }}>
                      <Icon as={BiHelpCircle} mb={1} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody borderColor='app.tertiary' borderWidth='1px'>
            {collections.map((collection, index) => {
              const CollectionIcon = BiIcon[collection.icon ?? "BiPen"];
              const rowLink = `/admin/my-collections/${collection.id}/attributes/`;
              const tdParams = {
                bg: index % 2 == 0 ? 'app.secondary' : '',
                onClick: () => router.push(rowLink),
                _hover: { cursor: 'pointer' }
              }
              return (
                <>
                  <Tr>
                    <Td {...tdParams}>
                      <Link href={rowLink}>
                        <CollectionIcon /> {collection.name}
                      </Link>
                    </Td>
                    <Td {...tdParams}><Code>{collection.id}</Code></Td>
                    <Td bg={index % 2 == 0 ? 'app.secondary' : ''}>{collection.order}</Td>
                    <Td bg={index % 2 == 0 ? 'app.secondary' : ''}>
                      <HStack flexDirection='row-reverse'>

                        <Tooltip hasArrow label='Delete' placement='top'>
                          <Button
                            p='0'
                            leftIcon={<BiTrash />}
                            bgColor='transparent'
                            onClick={() => collectionDelete(organization, collection)}
                            _hover={{ bgColor: 'transparent' }} />
                        </Tooltip>



                        <Tooltip hasArrow label='Edit Collection' placement='top'>
                          <Button
                            p='0'
                            leftIcon={<BiPencil />}
                            bgColor='transparent'
                            onClick={onClickEditCollection(collection)}
                            _hover={{ bgColor: 'transparent' }} />
                        </Tooltip>

                        <Tooltip hasArrow label='Edit Attributes' placement='top'>
                          <Link href={rowLink}>
                            <Button
                              p='0'
                              leftIcon={<BiListUl />}
                              bgColor='transparent'
                              onClick={() => { }}
                              _hover={{ bgColor: 'transparent' }} />
                          </Link>
                        </Tooltip>

                      </HStack>
                    </Td>
                  </Tr>
                </>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>


      <CollectionModal isOpen={isOpen} onClose={onClose} collection={currentCollection} />
      <DeleteDialog isOpen={isDeleteDialogOpen} onClose={onCloseDeleteDialog} collection={currentCollection} />

    </BasicLayout >

  );
}

export default withAuth(MyCollection);